"use client";

import { Suspense, useCallback, useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useSearchParams } from "next/navigation";
import { useAuth } from "@/lib/auth";
import { apiFetch } from "@/lib/api";
import type { AuthTokens, User } from "@/lib/types";
import { safeNext } from "@/lib/nav";
import Turnstile from "@/components/ui/Turnstile";
import GoogleSignIn from "@/components/auth/GoogleSignIn";
import PasswordInput from "@/components/ui/PasswordInput";
import PasswordStrength, { validatePasswordFully } from "@/components/ui/PasswordStrength";
import AuthBrandPanel from "@/components/auth/AuthBrandPanel";

export default function RegisterPage() {
  return (
    <Suspense fallback={<div className="flex min-h-screen items-center justify-center"><p className="text-sm text-slate-400">Loading...</p></div>}>
      <RegisterContent />
    </Suspense>
  );
}

function RegisterContent() {
  const { register, refreshUser, isAuthenticated, isLoading } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const next = safeNext(searchParams.get("next"));
  const lockedEmail = searchParams.get("email");
  // Invite-first: set when coming from the accept-invite page with an unregistered email.
  const inviteToken = searchParams.get("invite_token");

  // Redirect to destination if already authenticated
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      router.replace(next);
    }
  }, [isLoading, isAuthenticated, router, next]);

  const [name, setName] = useState("");
  const [email, setEmail] = useState(lockedEmail ?? "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const handleTurnstileVerify = useCallback((token: string) => {
    setTurnstileToken(token);
  }, []);

  const handleGoogleSuccess = useCallback(
    async (idToken: string) => {
      setError("");
      setLoading(true);
      try {
        const tokens = await apiFetch<AuthTokens>("/auth/google", {
          method: "POST",
          body: JSON.stringify({
            id_token: idToken,
            ...(inviteToken ? { invite_token: inviteToken } : {}),
          }),
        });
        localStorage.setItem("sf_access_token", tokens.access_token);
        localStorage.setItem("sf_refresh_token", tokens.refresh_token);
        await refreshUser();
        window.sfAnalytics?.track("register", "Auth", "google");

        // If a `next` redirect is set, honour it for BOTH new and existing users.
        // New-user: backend already bound the org via invite_token; loop to accept-invite
        // is idempotent. Existing-user: not bound by register, loops to accept-invite
        // where doAccept() joins the tenant.
        if (next !== "/dashboard") {
          try {
            const me = await apiFetch<User>("/auth/me", { token: tokens.access_token });
            router.push(me.terms_accepted_at ? next : `/accept-terms?next=${encodeURIComponent(next)}`);
          } catch {
            router.push(`/accept-terms?next=${encodeURIComponent(next)}`);
          }
          return;
        }

        if (inviteToken) {
          // Invite-first path (no `next`): fetch the bound org and route to the tenant
          // workspace. The backend already joined the tenant atomically — no separate
          // /accept needed.
          try {
            const me = await apiFetch<User>("/auth/me", { token: tokens.access_token });
            const orgSlug = me.org?.slug;
            if (orgSlug) {
              const dest = `/o/${orgSlug}/dashboard`;
              router.push(me.terms_accepted_at ? dest : `/accept-terms?next=${encodeURIComponent(dest)}`);
              return;
            }
          } catch {
            // Fall through to normal routing if /me fetch fails
          }
        }

        router.push(`/accept-terms?next=${encodeURIComponent(next)}`);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : "Google sign-up failed";
        setError(msg);
        window.sfAnalytics?.track("register_failed", "Auth", "google", undefined, { reason: msg });
      } finally {
        setLoading(false);
      }
    },
    [router, next, inviteToken, refreshUser]
  );

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    const pwError = validatePasswordFully(password);
    if (pwError) {
      setError(pwError);
      document.getElementById("password")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    if (!termsAccepted) {
      setError("You must accept the Terms of Service to create an account.");
      return;
    }

    setLoading(true);
    try {
      await register(email, password, name, turnstileToken, termsAccepted, inviteToken ?? undefined);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Registration failed";
      setError(msg);
      setTurnstileToken(null);
      setTurnstileResetKey((k) => k + 1);
      window.sfAnalytics?.track("register_failed", "Auth", "email", undefined, { reason: msg });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen">
      <AuthBrandPanel
        headline={<>Get documents signed<br /><span className="text-brand-500 italic">with proof built in.</span></>}
        subtitle="No credit card required. Upload a PDF, place your fields, and send — every signature backed by a verifiable audit trail."
        stats={[
          { label: "Free", sublabel: "Forever" },
          { label: "Legal", sublabel: "Full audit trail" },
          { label: "Verifiable", sublabel: "W3C proof" },
        ]}
      />

      {/* Right — Register form */}
      <div className="flex w-full lg:w-1/2 flex-col justify-center px-8 sm:px-16 lg:px-20">
        <div className="mx-auto w-full max-w-sm">
          <div className="lg:hidden mb-10 animate-slide-up-fade delay-0">
            <Link href="/">
              <h1 className="font-display text-2xl font-bold text-ink tracking-tight">
                Sign<span className="text-brand-600">Forge</span>
              </h1>
            </Link>
          </div>

          <h2 className="font-display text-2xl font-bold text-slate-900 tracking-tight animate-slide-up-fade delay-0">
            Create your account
          </h2>
          <p className="mt-2 text-sm text-slate-500 animate-slide-up-fade delay-80">
            Start sending documents for signature — free, forever
          </p>

          {/* Google Sign-In */}
          <div className="mt-8 animate-slide-up-fade delay-160">
            <GoogleSignIn
              onSuccess={handleGoogleSuccess}
              onError={(msg) => setError(msg)}
              text="signup_with"
            />
          </div>

          {/* Divider */}
          <div className="mt-6 flex items-center gap-3 animate-slide-up-fade delay-240">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="text-xs text-slate-400 font-medium">or sign up with email</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          {error && (
            <div key={error} className="mt-6 rounded-lg bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-700 animate-shake">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-6 space-y-5">
            <div className="animate-slide-up-fade delay-320">
              <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-1.5">
                Full name
              </label>
              <input
                id="name"
                type="text"
                required
                autoComplete="name"
                placeholder="Jane Smith"
                className="input-field"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="animate-slide-up-fade delay-400">
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5">
                Work email
              </label>
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                placeholder="jane@company.com"
                className={`input-field${lockedEmail ? " bg-slate-50 cursor-not-allowed" : ""}`}
                value={email}
                onChange={lockedEmail ? undefined : (e) => setEmail(e.target.value)}
                readOnly={!!lockedEmail}
              />
              {lockedEmail && (
                <p className="mt-1.5 text-xs text-slate-500">
                  Invited as <span className="font-medium text-slate-700">{lockedEmail}</span>
                </p>
              )}
            </div>

            <div className="animate-slide-up-fade delay-480">
              <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1.5">
                Password
              </label>
              <PasswordInput
                id="password"
                required
                autoComplete="new-password"
                placeholder="Create a strong password"
                className="input-field pr-10"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <PasswordStrength password={password} />
            </div>

            {/* Terms checkbox */}
            <label className="flex items-start gap-3 cursor-pointer group animate-slide-up-fade delay-560">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
              />
              <span className="text-sm text-slate-600 group-hover:text-slate-800 transition-colors">
                I agree to the{" "}
                <Link
                  href="/terms"
                  target="_blank"
                  className="font-semibold text-brand-600 hover:text-brand-700 underline decoration-brand-200 underline-offset-2"
                >
                  Terms of Service
                </Link>
              </span>
            </label>

            {/* Turnstile — no stagger animation */}
            <Turnstile
              onVerify={handleTurnstileVerify}
              onExpire={() => setTurnstileToken(null)}
              onError={() => setTurnstileToken(null)}
              theme="light"
              resetKey={turnstileResetKey}
            />

            <div className="animate-slide-up-fade delay-560">
              <button
                type="submit"
                disabled={loading || !termsAccepted}
                className="btn-primary w-full py-3"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
                      <path d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" fill="currentColor" className="opacity-75" />
                    </svg>
                    Creating account...
                  </span>
                ) : (
                  "Get started free"
                )}
              </button>
            </div>
          </form>

          <p className="mt-8 text-center text-sm text-slate-500 animate-slide-up-fade delay-560">
            Already have an account?{" "}
            <Link
              href={next !== "/dashboard" ? `/login?next=${encodeURIComponent(next)}` : "/login"}
              className="font-semibold text-brand-600 hover:text-brand-700 transition-colors link-underline"
            >
              Sign in
            </Link>
          </p>

          <p className="mt-6 text-center text-[11px] text-slate-400 flex items-center justify-center gap-1.5 animate-slide-up-fade delay-560">
            <svg className="h-3 w-3 text-emerald-500 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
            </svg>
            256-bit encryption · ISO 27001 infrastructure · EU data residency
          </p>
        </div>
      </div>
    </div>
  );
}
